import { useMemo, useState } from "react";
import { DateTime } from "luxon";
import type { ColumnDef } from "@tanstack/react-table";
import Table, { buildActionColumn, buildTagColumn, buildTwoLineColumn } from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import { actionId } from "@/hooks/useTableRowMenu";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import type { Path } from "@/router";
import { useNavigate } from "@/router";
import type { RosterActivityEvent } from "@/features/roster-activity";
import { COHORT_LABELS, EVENT_GROUPS } from "@/features/roster-activity";

type Props = {
  events: RosterActivityEvent[];
};

const COHORT_TAG_TYPE = {
  MEMBER: "highlight",
  ASSOCIATE: "surface",
  STUDENT: "outline"
} as const;

type SideSheetTarget = {
  pgaId: string;
  linkPath?: Path | null;
  personName: string;
};

function EmptyRosterActivity() {
  return (
    <EmptyState
      iconName="event_busy"
      title="No Roster Activity"
      caption="Try another week, month, or year, or adjust member type and change type filters."
    />
  );
}

function buildEventColumns(
  onViewPerson: (event: RosterActivityEvent) => void
): ColumnDef<RosterActivityEvent, unknown>[] {
  return [
    {
      accessorKey: "occurredAt",
      header: "Date",
      enableSorting: false,
      size: 140,
      cell: ({ row }) => (
        <span className="text-body-medium text-sys-brand-on-surface-variant whitespace-nowrap">
          {DateTime.fromISO(row.original.occurredAt).toFormat("MMM d, yyyy")}
        </span>
      )
    },
    buildTwoLineColumn<RosterActivityEvent>({
      accessorKey: "headline",
      header: "Activity",
      getPrimaryText: (event) => event.headline,
      // Keep the secondary line for row height; hide the "--" placeholder when there is no detail.
      getSecondaryText: (event) =>
        event.detail?.trim() || (
          <span className="invisible select-none" aria-hidden="true">
            --
          </span>
        ),
      size: 420
    }),
    buildTagColumn<RosterActivityEvent>({
      accessorKey: "cohort",
      header: "Member type",
      getTagLabel: (event) => COHORT_LABELS[event.cohort],
      getTagType: (event) => COHORT_TAG_TYPE[event.cohort],
      getTagStyle: () => "strong",
      size: 140
    }),
    buildActionColumn<RosterActivityEvent>({
      getActions: () => [],
      setMenu: () => undefined,
      actionId,
      showViewButton: (event) => Boolean(event.pgaId),
      onViewClick: onViewPerson,
      viewButtonTitle: "View person details",
      isRowNavigable: (event) => Boolean(event.pgaId && event.linkPath)
    })
  ];
}

export default function EventGroups({ events }: Props) {
  const navigate = useNavigate();
  const [sideSheet, setSideSheet] = useState<SideSheetTarget | null>(null);
  const columns = useMemo(
    () =>
      buildEventColumns((event) => {
        if (!event.pgaId) return;
        setSideSheet({
          pgaId: event.pgaId,
          linkPath: event.linkPath,
          personName: event.personName
        });
      }),
    []
  );

  if (!events.length) {
    return <EmptyRosterActivity />;
  }

  return (
    <div className="flex flex-col gap-6" data-testid="roster-activity-event-groups">
      {EVENT_GROUPS.map((group) => {
        const grouped = events.filter((event) => group.kinds.includes(event.kind));
        if (!grouped.length) return null;

        return (
          <section key={group.key} aria-labelledby={`roster-group-${group.key}`}>
            <h3 id={`roster-group-${group.key}`} className="text-title-large-bold font-serif mb-3">
              {group.title}
              <span className="text-body-medium text-sys-brand-on-surface-variant font-sans ml-2">
                ({grouped.length})
              </span>
            </h3>
            <Table
              data={grouped}
              loading={false}
              columns={columns}
              getRowId={(event) => event.id}
              getRowLink={(row) => {
                if (!row.original.pgaId || !row.original.linkPath) return null;
                return {
                  to: row.original.linkPath,
                  "aria-label": `View ${row.original.personName}`
                };
              }}
              data-testid={`roster-activity-group-table-${group.key}`}
            />
          </section>
        );
      })}
      <PersonSideSheet
        open={!!sideSheet}
        onClose={() => setSideSheet(null)}
        pgaId={sideSheet?.pgaId ?? null}
        showPersonalDetails
        showEmployment
        customActionButtons={
          sideSheet?.linkPath
            ? [
                {
                  text: "View Person",
                  variant: "outlined",
                  testId: "roster-activity-view-person",
                  onClick: () => {
                    const linkPath = sideSheet.linkPath;
                    if (!linkPath) return;
                    navigate(linkPath);
                    setSideSheet(null);
                  }
                }
              ]
            : undefined
        }
      />
    </div>
  );
}
